import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Chip,
  Button,
  IconButton,
  Alert,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import EditIcon from '@mui/icons-material/Edit';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';

// Shared look (design tokens) - same as the rest of IZHub
const ui = {
  primary: '#009db9',
  primaryDark: '#007a8a',
  primarySoft: '#e6f6f9',
  text: '#1f2933',
  muted: '#6b7280',
  border: '#e5e7eb',
  surface: '#f7f9fb',
  shadow: '0 4px 20px rgba(16, 24, 40, 0.06)',
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    backgroundColor: '#fff',
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: ui.primary },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ui.primary },
  },
  '& label.Mui-focused': { color: ui.primary },
};

const KRA_PIN_REGEX = /^[A-Za-z]\d{9}[A-Za-z]$/;
const ACCEPTED_FILE_TYPES = ['application/pdf', 'image/png', 'image/jpeg'];
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;

const KraPin = ({ kraPin, onChange, isViewOnly = false }) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [pinNumber, setPinNumber] = useState(kraPin?.pinNumber || '');
  const [pinError, setPinError] = useState('');
  const [fileError, setFileError] = useState('');
  const [file, setFile] = useState(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const currentFileName = file?.name || kraPin?.fileName || '';

  const validatePin = (value) => {
    if (!value) {
      return 'KRA Pin Number is required.';
    }
    if (!KRA_PIN_REGEX.test(value)) {
      return 'Enter a valid KRA Pin (e.g. A123456789Z).';
    }
    return '';
  };

  const validateFile = (selectedFile) => {
    if (!selectedFile) {
      return '';
    }
    if (!ACCEPTED_FILE_TYPES.includes(selectedFile.type)) {
      return 'Only PDF, PNG, or JPEG files are supported.';
    }
    if (selectedFile.size > MAX_FILE_SIZE_BYTES) {
      return 'File must be 5MB or smaller.';
    }
    return '';
  };

  const handlePinChange = (event) => {
    const value = event.target.value.toUpperCase();
    setPinNumber(value);
    setPinError(validatePin(value));
  };

  const handleFileSelect = (selectedFile) => {
    const error = validateFile(selectedFile);
    setFileError(error);
    if (!error) {
      setFile(selectedFile);
    }
  };

  const handleFileInputChange = (event) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      handleFileSelect(selectedFile);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragActive(false);
    const droppedFile = event.dataTransfer.files?.[0];
    if (droppedFile) {
      handleFileSelect(droppedFile);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setIsDragActive(false);
  };

  const handleRemoveFile = () => {
    setFile(null);
    setFileError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCancel = () => {
    setPinNumber(kraPin?.pinNumber || '');
    setPinError('');
    setFileError('');
    setFile(null);
    setIsEditMode(false);
  };

  const handleSave = () => {
    const pinValidationError = validatePin(pinNumber);
    setPinError(pinValidationError);
    if (pinValidationError || fileError) {
      return;
    }
    onChange({
      pinNumber,
      file,
      fileName: currentFileName,
    });
    setIsEditMode(false);
  };

  return (
    <Box
      sx={{
        mb: 3,
        borderRadius: '16px',
        border: `1px solid ${ui.border}`,
        backgroundColor: '#fff',
        boxShadow: ui.shadow,
        p: { xs: 2.5, sm: 3.5 },
      }}
    >
      {/* Section header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '10px',
            backgroundColor: ui.primarySoft,
            color: ui.primary,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <BadgeOutlinedIcon fontSize="small" />
        </Box>
        <Typography
          variant="h6"
          sx={{
            color: ui.text,
            fontWeight: 'bold',
            flexGrow: 1,
          }}
          data-tour="kra-pin-title"
        >
          KRA PIN
        </Typography>
        {!isViewOnly && !isEditMode && (
          <Tooltip title="Edit KRA Pin">
            <IconButton
              size="small"
              onClick={() => setIsEditMode(true)}
              aria-label="Edit KRA Pin"
              data-tour="kra-pin-edit-button"
              sx={{
                color: ui.primary,
                backgroundColor: ui.primarySoft,
                '&:hover': { backgroundColor: '#d3eff5' },
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {isViewOnly && (
        <Alert severity="info" sx={{ mb: 3, borderRadius: '10px' }}>
          You have view-only access to this employee's KRA Pin.
        </Alert>
      )}

      {!isEditMode ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
            gap: 2,
          }}
        >
          <Box
            sx={{
              p: 2,
              borderRadius: '12px',
              backgroundColor: ui.surface,
              border: `1px solid ${ui.border}`,
            }}
          >
            <Typography variant="caption" sx={{ color: ui.muted }}>
              KRA Pin Number
            </Typography>
            <Typography
              sx={{
                fontWeight: 700,
                color: kraPin?.pinNumber ? ui.text : ui.muted,
                letterSpacing: kraPin?.pinNumber ? 1.5 : 0,
                fontSize: '1.05rem',
              }}
            >
              {kraPin?.pinNumber || 'Not provided'}
            </Typography>
          </Box>
          <Box
            sx={{
              p: 2,
              borderRadius: '12px',
              backgroundColor: ui.surface,
              border: `1px solid ${ui.border}`,
              minWidth: 0,
            }}
          >
            <Typography variant="caption" sx={{ color: ui.muted }}>
              Document
            </Typography>
            {currentFileName ? (
              <Box sx={{ mt: 0.5 }}>
                <Chip
                  icon={<InsertDriveFileIcon sx={{ color: `${ui.primaryDark} !important` }} />}
                  label={currentFileName}
                  sx={{
                    bgcolor: ui.primarySoft,
                    color: ui.primaryDark,
                    fontWeight: 600,
                    borderRadius: '10px',
                    maxWidth: '100%',
                  }}
                />
              </Box>
            ) : (
              <Typography sx={{ color: ui.muted }}>No document uploaded</Typography>
            )}
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <TextField
            fullWidth
            label="KRA Pin Number"
            value={pinNumber}
            onChange={handlePinChange}
            error={Boolean(pinError)}
            helperText={pinError || 'Format: A123456789Z'}
            variant="outlined"
            data-tour="kra-pin-number-field"
            sx={fieldSx}
          />

          <Box
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            data-tour="kra-pin-upload-area"
            sx={{
              border: '2px dashed',
              borderColor: isDragActive ? ui.primary : '#cbd5e1',
              borderRadius: '14px',
              p: { xs: 3, sm: 4 },
              textAlign: 'center',
              bgcolor: isDragActive ? ui.primarySoft : ui.surface,
              transition: 'background-color 0.2s ease, border-color 0.2s ease',
            }}
          >
            <Box
              sx={{
                width: 52,
                height: 52,
                mx: 'auto',
                mb: 1.5,
                borderRadius: '50%',
                backgroundColor: ui.primarySoft,
                color: ui.primary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <UploadFileIcon sx={{ fontSize: 28 }} />
            </Box>
            <Typography variant="body1" sx={{ mb: 2, color: ui.text }}>
              Drag and drop a PDF, PNG, or JPEG here, or
            </Typography>
            <Button
              variant="outlined"
              onClick={() => fileInputRef.current?.click()}
              sx={{
                borderColor: ui.primary,
                color: ui.primary,
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '10px',
                '&:hover': {
                  borderColor: ui.primaryDark,
                  bgcolor: 'rgba(0, 157, 185, 0.06)',
                },
              }}
            >
              Browse Files
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
              hidden
              onChange={handleFileInputChange}
            />
            <Typography variant="body2" sx={{ mt: 2, color: ui.muted }}>
              Max file size: 5MB
            </Typography>
          </Box>

          {fileError && (
            <Alert severity="error" sx={{ borderRadius: '10px' }}>
              {fileError}
            </Alert>
          )}

          {currentFileName && (
            <Chip
              icon={<DescriptionOutlinedIcon sx={{ color: `${ui.primaryDark} !important` }} />}
              label={currentFileName}
              onDelete={handleRemoveFile}
              deleteIcon={<CloseIcon />}
              sx={{
                bgcolor: ui.primarySoft,
                color: ui.primaryDark,
                fontWeight: 600,
                borderRadius: '10px',
                width: 'fit-content',
                maxWidth: '100%',
                '&:hover': { bgcolor: '#d3eff5' },
                '& .MuiChip-deleteIcon': {
                  color: ui.primaryDark,
                  '&:hover': { color: '#d32f2f' },
                },
              }}
            />
          )}

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column-reverse', sm: 'row' },
              justifyContent: 'flex-end',
              gap: 1.5,
              pt: 2.5,
              borderTop: `1px solid ${ui.border}`,
            }}
          >
            <Button
              onClick={handleCancel}
              sx={{ color: ui.muted, textTransform: 'none', fontWeight: 600 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              data-tour="kra-pin-save-button"
              sx={{
                bgcolor: ui.primary,
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: '10px',
                px: 3,
                py: 1.1,
                boxShadow: 'none',
                '&:hover': { bgcolor: ui.primaryDark, boxShadow: 'none' },
              }}
            >
              Save
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default KraPin;